import React from "react";
import GoalProgressBar from "../../revenue-planner/GoalProgressBar";
import type { PaceStatus } from "../../../lib/revenuePlannerEngine";
import { HEX, hexToRgba } from "../../../lib/colorSystem";
import DateRangePicker from "../../heatmap/DateRangePicker";
import type { DateRange } from "../../../hooks/useGlobalDateRange";
import DayRangeStrip from "./DayRangeStrip";

// =============================================================================
// DASHBOARD HEADER
// =============================================================================
// PURPOSE: Compact top bar — org name, tabs, date picker all on one row.
// Goal progress bar below when configured.
// =============================================================================

export type DashboardTab = "performance" | "forecast" | "year";

interface GoalProgressProps {
  goalAmount: number;
  cashSoFar: number;
  paceStatus: PaceStatus;
  monthLabel: string;
}

interface DashboardHeaderProps {
  organizationName: string;
  orgTimezone?: string;
  activeTab?: DashboardTab;
  onTabChange?: (tab: DashboardTab) => void;
  goalProgress?: GoalProgressProps;
  dateRange: DateRange;
  setDateRange: (value: DateRange | ((prev: DateRange) => DateRange)) => void;
  navigateDateRange: (direction: "prev" | "next") => void;
  selectedYear?: number;
  onYearChange?: (year: number) => void;
  dayStart?: number;
  dayEnd?: number;
  onDayRangeChange?: (next: { start: number; end: number }) => void;
  /** Self-set filter state. Only rendered on the Performance tab — "self-set" is a
   *  sales-call concept, so it has no meaning on Forecast or Year. Omit both props to
   *  hide the control entirely (the client portal passes neither). */
  selfSetOnly?: boolean;
  onSelfSetToggle?: (next: boolean) => void;
}

const TAB_OPTIONS: { key: DashboardTab; label: string }[] = [
  { key: "performance", label: "Performance" },
  { key: "forecast", label: "Forecast & Planning" },
  { key: "year", label: "Year" },
];

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  organizationName,
  orgTimezone,
  activeTab,
  onTabChange,
  goalProgress,
  dateRange,
  setDateRange,
  navigateDateRange,
  selectedYear,
  onYearChange,
  dayStart,
  dayEnd,
  onDayRangeChange,
  selfSetOnly,
  onSelfSetToggle,
}) => (
  <div className="flex flex-col gap-2">
    {/* Single row: Org name + Tabs + Date picker */}
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-4 flex-wrap">
        <h1 className="text-lg sm:text-xl font-bold uppercase truncate heading-display text-glass min-w-0">
          {organizationName}
        </h1>
        {orgTimezone && (
          <span className="token-9 text-glass-muted font-mono hidden sm:inline">{orgTimezone}</span>
        )}
        {activeTab && onTabChange && (
          <div className="flex rounded-lg overflow-hidden liquid-glass-light">
            {TAB_OPTIONS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => onTabChange(tab.key)}
                className="font-semibold transition-all"
                style={{
                  fontSize: 11,
                  padding: "5px 14px",
                  color: activeTab === tab.key ? HEX.accent : "var(--glass-text-inactive)",
                  background: activeTab === tab.key ? "var(--glass-bg-active)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: activeTab === tab.key ? "var(--glass-shadow-inset)" : "none",
                  letterSpacing: "0.02em",
                  textShadow:
                    activeTab === tab.key ? `0 0 12px ${hexToRgba(HEX.accent, 0.4)}` : "none",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
        {/* Self-set filter. Performance tab only — a self-set deal is a sales-call
            outcome with no setter, a concept the Forecast and Year tabs don't model.
            Two-state button in one slot (never a dead disabled control) — same pattern
            as DayRangeStrip's MTD/Reset toggle. `data-mode` is the load-bearing test
            hook so renaming the label later doesn't break the spec. */}
        {activeTab === "performance" && onSelfSetToggle && (
          <button
            type="button"
            onClick={() => onSelfSetToggle(!selfSetOnly)}
            data-testid="self-set-toggle"
            data-mode={selfSetOnly ? "self-set" : "all"}
            aria-pressed={!!selfSetOnly}
            title={
              selfSetOnly
                ? "Showing self-set only (closer-sourced, no setter) — click to show all"
                : "Show only self-set deals (closer-sourced, no setter)"
            }
            className="liquid-glass-light font-semibold transition-all"
            style={{
              fontSize: 11,
              padding: "5px 14px",
              borderRadius: 8,
              color: selfSetOnly ? HEX.accent : "var(--glass-text-inactive)",
              background: selfSetOnly ? "var(--glass-bg-active)" : "transparent",
              border: "none",
              cursor: "pointer",
              letterSpacing: "0.02em",
              boxShadow: selfSetOnly ? "var(--glass-shadow-inset)" : "none",
              textShadow: selfSetOnly ? `0 0 12px ${hexToRgba(HEX.accent, 0.4)}` : "none",
            }}
          >
            Self-Set{selfSetOnly ? " ✓" : ""}
          </button>
        )}
      </div>
      {activeTab === "year" && onYearChange && selectedYear !== undefined ? (
        <select
          value={selectedYear}
          onChange={(e) => onYearChange(Number(e.target.value))}
          className="liquid-glass-light px-3 py-1.5 token-13 text-glass cursor-pointer"
          style={{
            fontVariantNumeric: "tabular-nums",
            // OVERRIDE: liquid-glass-light sets overflow:hidden in index.css, which clips
            // the native <select> dropdown options (see .claude/rules/css-glass.md — Tailwind
            // overflow-visible has equal specificity but loads earlier so glass wins; inline
            // style is the only reliable override).
            overflow: "visible",
          }}
        >
          {Array.from(
            // Year selector spans Rayston founding (2024) through the current calendar year.
            // Recomputed on every render — handles Jan-1 rollover without needing redeploy.
            { length: new Date().getFullYear() - 2024 + 1 },
            (_, i) => 2024 + i
          ).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      ) : (
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={setDateRange}
          navigateDateRange={navigateDateRange}
        />
      )}
    </div>
    {/* Year tab: day-of-month slicer strip on its own row beneath the tabs */}
    {activeTab === "year" && onDayRangeChange && dayStart !== undefined && dayEnd !== undefined && (
      <DayRangeStrip dayStart={dayStart} dayEnd={dayEnd} onChange={onDayRangeChange} />
    )}
    {/* Goal progress bar */}
    {goalProgress && goalProgress.goalAmount > 0 && <GoalProgressBar {...goalProgress} />}
  </div>
);

export default DashboardHeader;
